import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Check, Globe } from 'lucide-react';
import { useLang, type Lang } from '../../i18n';
import type { LangSwitchProps } from './LangSwitch.types';
import './LangSwitch.scss';

const OPTIONS: { code: Lang; label: string }[] = [
  { code: 'uz-Latn', label: "O'zbek" },
  { code: 'uz-Cyrl', label: 'Ўзбек' },
  { code: 'ru',      label: 'Русский' },
  { code: 'en',      label: 'English' },
];

export default function LangSwitch({ className, align = 'right', direction = 'down' }: LangSwitchProps) {
  const { lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const current = OPTIONS.find(o => o.code === lang) ?? OPTIONS[0];

  return (
    <div ref={ref} className={['lang-switch', `lang-switch--${align}`, `lang-switch--${direction}`, className || ''].filter(Boolean).join(' ')}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        className="lang-switch__trigger"
        onClick={() => setOpen(o => !o)}
      >
        <Globe size={14} aria-hidden="true" />
        <span className="lang-switch__label">{current.label}</span>
        <ChevronDown size={14} aria-hidden="true" />
      </button>
      {open ? (
        <ul role="listbox" className="lang-switch__menu">
          {OPTIONS.map(o => (
            <li
              key={o.code}
              role="option"
              aria-selected={o.code === lang}
              className={['lang-switch__option', o.code === lang ? 'lang-switch__option--active' : ''].filter(Boolean).join(' ')}
              onClick={() => { setLang(o.code); setOpen(false); }}
            >
              <span>{o.label}</span>
              {o.code === lang ? <Check size={14} aria-hidden="true" /> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
